import { useEffect, useState } from "react";
import { getYggEvaluation, submitYggSurvey } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { toast } from "sonner";
import { Save, Loader2, CheckCircle2, User, Sparkles } from "lucide-react";

const ENGLISH_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const COMPETENCIES = ["Müşteri Odaklılık", "Analitik Düşünme", "İletişim", "Takım Çalışması", "Öğrenme & Değişim", "Sonuç Odaklılık", "Dijital Farkındalık", "Kaynakları Verimli Kullanma", "Geri Bildirim", "Liderlik"];

/**
 * YggSurveyForm — Employee self-assessment for a given YGG cycle.
 */
export default function YggSurveyForm({ cycleId, employeeId, cycleName, onSaved }) {
  const [survey, setSurvey] = useState(null);
  const [stageStatus, setStageStatus] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cycleId) return;
    getYggEvaluation(cycleId, employeeId).then((e) => {
      setSurvey(e.survey || { top3_strengths: [], top3_dev_areas: [], self_talent_codes: [] });
      setStageStatus(e.stage_status || {});
    });
  }, [cycleId, employeeId]);

  const update = (patch) => setSurvey({ ...survey, ...patch });

  const toggleList = (field, val, max = 3) => {
    const list = new Set(survey?.[field] || []);
    if (list.has(val)) list.delete(val);
    else if (list.size < max) list.add(val);
    else return; // reached limit
    update({ [field]: Array.from(list) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await submitYggSurvey(cycleId, employeeId, survey);
      toast.success("Anket kaydedildi. Bir sonraki aşama yöneticinize düştü.");
      setStageStatus({ ...stageStatus, survey: "completed" });
      onSaved?.();
    } catch (e) {
      toast.error("Anket kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (!survey) return <div className="card-surface p-6 text-sm text-slate-400" data-testid="ygg-survey-loading">Anket yükleniyor…</div>;

  const done = stageStatus.survey === "completed";

  return (
    <div className="card-surface p-6 space-y-5" data-testid="ygg-survey-form">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Öz-Değerlendirme Anketim</div>
          <h3 className="font-display text-xl font-semibold">{cycleName}</h3>
          <p className="text-xs text-slate-500 mt-1">Cevapların yöneticine ve İK&apos;ya iletilecek; kariyer hedeflerin ve gelişim planın için kullanılacak.</p>
        </div>
        {done && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Tamamlandı</span>}
      </div>

      <FormRow label="Mevcut rolünde kendini ne kadar yetkin hissediyorsun? Nedenini açıkla.">
        <Textarea rows={2} value={survey.current_role_competency || ""} onChange={(e) => update({ current_role_competency: e.target.value })} data-testid="ygg-competency-input" />
      </FormRow>

      <FormRow label={`En güçlü olduğun 3 yetkinlik (${(survey.top3_strengths || []).length}/3)`}>
        <div className="flex flex-wrap gap-1.5">
          {COMPETENCIES.map((c) => {
            const on = (survey.top3_strengths || []).includes(c);
            return (
              <button key={c} type="button"
                onClick={() => toggleList("top3_strengths", c)}
                className={`px-2.5 py-1 rounded-full border text-xs transition ${on ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"}`}
                data-testid={`ygg-strength-${c}`}>
                {c}
              </button>
            );
          })}
        </div>
      </FormRow>

      <FormRow label={`Gelişime en açık olduğun 3 yetkinlik (${(survey.top3_dev_areas || []).length}/3)`}>
        <div className="flex flex-wrap gap-1.5">
          {COMPETENCIES.map((c) => {
            const on = (survey.top3_dev_areas || []).includes(c);
            return (
              <button key={c} type="button"
                onClick={() => toggleList("top3_dev_areas", c)}
                className={`px-2.5 py-1 rounded-full border text-xs transition ${on ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-200 hover:border-amber-300"}`}
                data-testid={`ygg-devarea-${c}`}>
                {c}
              </button>
            );
          })}
        </div>
      </FormRow>

      <FormRow label="Kariyer hedefine ulaşmak için hangi desteklere ihtiyacın var?">
        <Textarea rows={2} value={survey.support_needed || ""} onChange={(e) => update({ support_needed: e.target.value })} placeholder="Örn: Liderlik eğitimi, mentor eşleşmesi, bütçe yönetimi eğitimi" data-testid="ygg-support-input" />
      </FormRow>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormRow label="Dikey hareket hedefi (rol + ne kadar sürede)">
          <Textarea rows={2} value={survey.vertical_target || ""} onChange={(e) => update({ vertical_target: e.target.value })} placeholder="Örn: 2 yıl içinde Veri ve Analitik Müdürü" data-testid="ygg-vertical-input" />
        </FormRow>
        <FormRow label="Yatay hareket / rotasyon hedefi">
          <Textarea rows={2} value={survey.horizontal_target || ""} onChange={(e) => update({ horizontal_target: e.target.value })} placeholder="Örn: 1 yıl içinde Ürün Yönetimi'ne rotasyon" data-testid="ygg-horizontal-input" />
        </FormRow>
      </div>

      <FormRow label="Son 2 yılda katıldığın projeler">
        <Textarea rows={2} value={survey.projects_last_2y || ""} onChange={(e) => update({ projects_last_2y: e.target.value })} data-testid="ygg-projects-input" />
      </FormRow>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormRow label="İngilizce seviyen">
          <Select value={survey.english_level || ""} onValueChange={(v) => update({ english_level: v })}>
            <SelectTrigger className="h-9 bg-white" data-testid="ygg-english-select"><SelectValue placeholder="Seç" /></SelectTrigger>
            <SelectContent className="bg-white">{ENGLISH_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </FormRow>
        <FormRow label="Diğer görüş, yorum ve önerilerin">
          <Input value={survey.additional_comments || ""} onChange={(e) => update({ additional_comments: e.target.value })} placeholder="Serbest yorum" data-testid="ygg-comments-input" />
        </FormRow>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" /> {done ? "Anketin kaydedildi. Güncellemek için tekrar 'Kaydet' basabilirsin." : "Cevaplarını istediğin zaman güncelleyebilirsin."}
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="ygg-survey-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Anketi Kaydet
        </Button>
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
